import React, { useMemo, useState } from 'react';
import AnalyticsSettingsPanel, { useAnalyticsSettings, SettingsButton } from '../common/AnalyticsSettings';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';

const AVAILABLE_METRICS = [
  { id: 'totalOrders', label: 'Total Orders', icon: '📋', defaultVisible: true },
  { id: 'totalEstimatedHours', label: 'Estimated Hours', icon: '⏱️', defaultVisible: true },
  { id: 'totalEstimatedPrice', label: 'Estimated $', icon: '💵', defaultVisible: true },
  { id: 'totalSubmittedHours', label: 'Submitted Hours', icon: '⏰', defaultVisible: true },
  { id: 'totalApprovedHours', label: 'Approved Hours', icon: '✅', defaultVisible: true },
  { id: 'totalApprovedQty', label: 'Total Approved Qty', icon: '📊', defaultVisible: true },
  { id: 'avgProgress', label: 'Avg Progress', icon: '📊', defaultVisible: true },
];

const AVAILABLE_CHARTS = [
  { id: 'byDiscipline', label: 'By Discipline', icon: '📂', defaultVisible: true, defaultType: 'bar' },
  { id: 'byStatus', label: 'By Status', icon: '📊', defaultVisible: true, defaultType: 'bar' },
  { id: 'byType', label: 'By Type', icon: '📁', defaultVisible: true, defaultType: 'bar' },
  { id: 'timeline', label: 'Timeline', icon: '📅', defaultVisible: true, allowTypeChange: false },
];

/**
 * ChangeOrdersAnalyticsDashboard - Analytics Dashboard for Change Orders Tracking
 * Displays cost impact, status distribution, and approval timeline
 */
const ChangeOrdersAnalyticsDashboard = ({ data, onClose }) => {
  const [selectedView, setSelectedView] = useState('overview');
  const [showSettings, setShowSettings] = useState(false);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState } = usePresence('changeOrdersAnalytics', 'global', true);
  
  const {
    settings,
    updateSetting,
    toggleMetric,
    toggleChart,
    setChartType,
    resetToDefaults,
    isMetricVisible,
    isChartVisible,
  } = useAnalyticsSettings('change_orders_log', AVAILABLE_METRICS, AVAILABLE_CHARTS);

  const analytics = useMemo(() => {
    if (!data || data.length === 0) {
      return { totalOrders: 0, totalEstimatedHours: 0, totalEstimatedPrice: 0, totalSubmittedHours: 0, totalApprovedHours: 0, totalApprovedQty: 0, totalBudgetHours: 0, totalActualHours: 0, totalBudgetCost: 0, totalActualCost: 0, avgProgress: 0, byDiscipline: [], byStatus: [], byType: [], timeline: [] };
    }

    const totalOrders = data.length;
    const totalBudgetHours = data.reduce((sum, d) => sum + (parseFloat(d.budget_hour_estimated) || 0), 0);
    const totalActualHours = data.reduce((sum, d) => sum + (parseFloat(d.actual_spent_hour) || 0), 0);
    const totalBudgetCost = data.reduce((sum, d) => sum + (parseFloat(d.budget_cost_total) || 0), 0);
    const totalActualCost = data.reduce((sum, d) => sum + (parseFloat(d.actual_cost_total) || 0), 0);
    const totalEstimatedHours = data.reduce((sum, d) => sum + (parseFloat(d.budget_hour_estimated) || 0), 0);
    const totalEstimatedPrice = data.reduce((sum, d) => sum + (parseFloat(d.estimated_price) || 0), 0);
    const totalSubmittedHours = data.reduce((sum, d) => sum + (parseFloat(d.submitted_hours) || 0), 0);
    const approvedEntries = data.filter(d => d.maestro_status === 'approved');
    const totalApprovedHours = approvedEntries.reduce((sum, d) => sum + (parseFloat(d.actual_spent_hour) || 0), 0);
    const totalApprovedQty = approvedEntries.length;
    
    const progresses = data.map(d => parseFloat(d.progress_percentage) || 0);
    const avgProgress = progresses.length > 0 ? progresses.reduce((a, b) => a + b, 0) / progresses.length : 0;

    // By Discipline
    const discMap = {};
    data.forEach(d => {
      const disc = d.discipline || 'Unspecified';
      if (!discMap[disc]) discMap[disc] = { name: disc, count: 0, budgetCost: 0, actualCost: 0 };
      discMap[disc].count++;
      discMap[disc].budgetCost += parseFloat(d.budget_cost_total) || 0;
      discMap[disc].actualCost += parseFloat(d.actual_cost_total) || 0;
    });
    const byDiscipline = Object.values(discMap).sort((a, b) => b.budgetCost - a.budgetCost);

    // By Status
    const statusMap = {};
    data.forEach(d => {
      const status = d.status || 'Unspecified';
      if (!statusMap[status]) statusMap[status] = { name: status, count: 0, budgetCost: 0 };
      statusMap[status].count++;
      statusMap[status].budgetCost += parseFloat(d.budget_cost_total) || 0;
    });
    const byStatus = Object.values(statusMap).sort((a, b) => b.count - a.count);

    // By Type
    const typeMap = {};
    data.forEach(d => {
      const type = d.change_type_fwo_ccr || 'Unspecified';
      if (!typeMap[type]) typeMap[type] = { name: type, count: 0, budgetCost: 0 };
      typeMap[type].count++;
      typeMap[type].budgetCost += parseFloat(d.budget_cost_total) || 0;
    });
    const byType = Object.values(typeMap).sort((a, b) => b.count - a.count);

    // Timeline by date issued
    const dateMap = {};
    data.forEach(d => {
      const date = (d.date_issued_ccr || '')?.split('T')[0];
      if (date) {
        if (!dateMap[date]) dateMap[date] = { date, count: 0, budgetCost: 0 };
        dateMap[date].count++;
        dateMap[date].budgetCost += parseFloat(d.budget_cost_total) || 0;
      }
    });
    const timeline = Object.values(dateMap).sort((a, b) => String(a.date).localeCompare(String(b.date)));

    return { totalOrders, totalBudgetHours, totalActualHours, totalBudgetCost, totalActualCost, totalEstimatedHours, totalEstimatedPrice, totalSubmittedHours, totalApprovedHours, totalApprovedQty, avgProgress, byDiscipline, byStatus, byType, timeline, variance: totalActualCost - totalBudgetCost };
  }, [data]);

  const colors = { primary: '#6366f1', secondary: '#8b5cf6', success: '#10b981', warning: '#f59e0b', danger: '#ef4444', info: '#3b82f6', dark: '#1e293b', chartColors: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'] };

  const formatCurrency = (num) => {
    if (num >= 1000000) return '$' + (num / 1000000).toFixed(2) + 'M';
    if (num >= 1000) return '$' + (num / 1000).toFixed(1) + 'K';
    return '$' + num.toFixed(2);
  };

  const formatNumber = (num, decimals = 0) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toFixed(decimals);
  };

  const MetricCard = ({ title, value, subtitle, icon, color }) => (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', borderLeft: `4px solid ${color}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>{title}</span>
        <span style={{ fontSize: '1.5rem' }}>{icon}</span>
      </div>
      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: colors.dark, margin: '0.5rem 0' }}>{value}</div>
      {subtitle && <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{subtitle}</span>}
    </div>
  );

  const BarChart = ({ data, valueKey, labelKey, title, formatValue }) => {
    const chartData = data.slice(0, 10);
    const maxValue = Math.max(...chartData.map(d => d[valueKey] || 0), 1);
    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {chartData.map((item, idx) => {
            const value = item[valueKey] || 0;
            const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: '100px', fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item[labelKey]}</div>
                <div style={{ flex: 1, height: '24px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', backgroundColor: colors.chartColors[idx % colors.chartColors.length], borderRadius: '4px' }} />
                </div>
                <div style={{ width: '70px', fontSize: '0.75rem', color: '#64748b', textAlign: 'right' }}>
                  {formatValue ? formatValue(value) : formatNumber(value)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'breakdown', label: 'Breakdown', icon: '📋' },
    { id: 'costs', label: 'Costs', icon: '💰' },
  ];

  const renderOverview = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <MetricCard title="Total Orders" value={analytics.totalOrders} icon="📋" color={colors.primary} />
        <MetricCard title="Estimated Hours" value={formatNumber(analytics.totalEstimatedHours, 1)} icon="⏱️" color={colors.info} />
        <MetricCard title="Estimated $" value={formatCurrency(analytics.totalEstimatedPrice)} icon="💵" color={colors.info} />
        <MetricCard title="Submitted Hours" value={formatNumber(analytics.totalSubmittedHours, 1)} icon="⏰" color={colors.warning} />
        <MetricCard title="Approved Hours" value={formatNumber(analytics.totalApprovedHours, 1)} icon="✅" color={colors.success} />
        <MetricCard title="Total Approved Qty" value={analytics.totalApprovedQty} icon="📊" color={colors.success} />
        <MetricCard title="Avg Progress" value={`${analytics.avgProgress.toFixed(1)}%`} icon="📊" color={colors.warning} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem' }}>
        <BarChart data={analytics.byStatus} valueKey="count" labelKey="name" title="Orders by Status" />
        <BarChart data={analytics.byType} valueKey="count" labelKey="name" title="Orders by Type" />
      </div>
    </>
  );

  const renderBreakdown = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <BarChart data={analytics.byDiscipline} valueKey="count" labelKey="name" title="Orders by Discipline" />
        <BarChart data={analytics.byDiscipline} valueKey="budgetCost" labelKey="name" title="Budget Cost by Discipline" formatValue={formatCurrency} />
      </div>
      {analytics.timeline.length > 0 && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
          <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>Orders Timeline</h4>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '120px' }}>
            {analytics.timeline.slice(-30).map((d, idx) => {
              const maxCount = Math.max(...analytics.timeline.map(t => t.count), 1);
              const height = (d.count / maxCount) * 100;
              return <div key={idx} style={{ flex: 1, backgroundColor: colors.primary, borderRadius: '2px 2px 0 0', height: `${height}%`, minHeight: '2px' }} title={`${d.date}: ${d.count} orders`} />;
            })}
          </div>
        </div>
      )}
    </>
  );

  const renderCosts = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <MetricCard title="Estimated Hours" value={formatNumber(analytics.totalEstimatedHours, 1)} icon="⏱️" color={colors.info} />
        <MetricCard title="Estimated $" value={formatCurrency(analytics.totalEstimatedPrice)} icon="💵" color={colors.info} />
        <MetricCard title="Submitted Hours" value={formatNumber(analytics.totalSubmittedHours, 1)} icon="⏰" color={colors.warning} />
        <MetricCard title="Approved Hours" value={formatNumber(analytics.totalApprovedHours, 1)} icon="✅" color={colors.success} />
        <MetricCard title="Total Approved Qty" value={analytics.totalApprovedQty} icon="📊" color={colors.success} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem' }}>
        <BarChart data={analytics.byDiscipline} valueKey="actualCost" labelKey="name" title="Actual Cost by Discipline" formatValue={formatCurrency} />
        <BarChart data={analytics.byStatus} valueKey="budgetCost" labelKey="name" title="Budget Cost by Status" formatValue={formatCurrency} />
      </div>
    </>
  );

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)', zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
      <div style={{ backgroundColor: '#f1f5f9', borderRadius: '16px', width: '100%', maxWidth: '1400px', maxHeight: '95vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
        <div style={{ padding: '1.25rem 1.5rem', background: `linear-gradient(135deg, #795548 0%, #5d4037 100%)`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>📝 Change Orders Analytics</h2>
            <p style={{ margin: '0.25rem 0 0', opacity: 0.9, fontSize: '0.85rem' }}>Cost impact, status distribution, and approval tracking</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
            <button onClick={onClose} style={{ padding: '0.5rem 1rem', backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem' }}>✕ Close</button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.5rem 0', backgroundColor: 'white', borderBottom: '1px solid #e2e8f0' }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setSelectedView(tab.id)} style={{
              padding: '0.75rem 1.25rem', backgroundColor: selectedView === tab.id ? '#795548' : 'transparent',
              color: selectedView === tab.id ? 'white' : '#64748b', border: 'none', borderRadius: '8px 8px 0 0',
              cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.5rem',
            }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
          {analytics.totalOrders === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
              <h3 style={{ margin: 0, color: colors.dark }}>No Data Available</h3>
            </div>
          ) : (
            <>
              {selectedView === 'overview' && renderOverview()}
              {selectedView === 'breakdown' && renderBreakdown()}
              {selectedView === 'costs' && renderCosts()}
            </>
          )}
        </div>

        <div style={{ padding: '0.75rem 1.5rem', backgroundColor: 'white', borderTop: '1px solid #e2e8f0', fontSize: '0.75rem', color: '#94a3b8', display: 'flex', justifyContent: 'space-between' }}>
          <span>{analytics.totalOrders} orders | {analytics.byDiscipline.length} disciplines</span>
          <span>Budget: {formatCurrency(analytics.totalBudgetCost)} | Actual: {formatCurrency(analytics.totalActualCost)} | Variance: {formatCurrency(analytics.variance)}</span>
        </div>
      </div>
    </div>
  );
};

export default ChangeOrdersAnalyticsDashboard;

